import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  Stack,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Pause,
  Refresh,
  Send,
  ExpandMore,
  Router,
  Timeline,
} from '@mui/icons-material';
import { useAPI } from '../../hooks/useAPI';

interface TrackerData {
  trackerId: string;
  customTitle: string;
  currentRole: string;
  status: 'active' | 'standby' | 'offline';
  speed: number;
  odometerStatus?: 'running' | 'paused' | 'stopped';
}

interface ControlPanelProps {
  trackers: TrackerData[];
}

const ControlPanel: React.FC<ControlPanelProps> = ({ trackers }) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [commandDialog, setCommandDialog] = useState(false);
  const [selectedTracker, setSelectedTracker] = useState('');
  const [customCommand, setCustomCommand] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const api = useAPI();

  // Odometer Controls
  const handleOdometerAction = async (trackerId: string, action: 'start' | 'stop' | 'pause' | 'resume' | 'reset') => {
    try {
      setLoading(`${trackerId}-odometer-${action}`);
      await api.post(`/odometer/${trackerId}/${action}`);
      setMessage({ type: 'success', text: `Odometer ${action} successful for ${trackerId}` });
    } catch (error: any) {
      setMessage({ type: 'error', text: `Failed to ${action} odometer for ${trackerId}` });
    } finally {
      setLoading(null);
    }
  };



  // Send custom command
  const handleSendCommand = async () => {
    if (!selectedTracker || !customCommand.trim()) return;

    try {
      setLoading('custom-command');
      await api.post(`/commands/${selectedTracker}/send`, { command: customCommand.trim() });
      setMessage({ type: 'success', text: `Command sent to ${selectedTracker}` });
      setCommandDialog(false);
      setCustomCommand('');
    } catch (error: any) {
      setMessage({ type: 'error', text: `Failed to send command to ${selectedTracker}` });
    } finally {
      setLoading(null);
    }
  };

  // Quick commands
  const quickCommands = [
    { label: 'Get Position', command: 'GET_POSITION', icon: <Router /> },
    { label: 'Reboot', command: 'REBOOT', icon: <Refresh /> },
    { label: 'Get Info', command: 'GET_INFO', icon: <Timeline /> },
  ];

  const handleQuickCommand = async (trackerId: string, commandKey: string) => {
    try {
      setLoading(`${trackerId}-quick-${commandKey}`);
      await api.post(`/commands/${trackerId}/predefined/${commandKey}`);
      setMessage({ type: 'success', text: `${commandKey} command sent to ${trackerId}` });
    } catch (error: any) {
      setMessage({ type: 'error', text: `Failed to send ${commandKey} to ${trackerId}` });
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box>
      {/* Message Display */}
      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Box>
        {/* Individual Tracker Controls */}
        {trackers.map((tracker) => (
          <Box key={tracker.trackerId} sx={{ mb: 2 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {/* Tracker Header */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {tracker.customTitle || tracker.currentRole}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={tracker.status}
                        size="small"
                        color={tracker.status === 'active' ? 'success' : 'default'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {tracker.trackerId}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Odometer Controls - Hidden for now */}
                  {false && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 'auto' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Timeline fontSize="small" />
                          <Typography variant="body2">Odometer</Typography>
                          {tracker.odometerStatus && (
                            <Chip
                              label={tracker.odometerStatus}
                              size="small"
                              color={tracker.odometerStatus === 'running' ? 'success' : 'default'}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <ButtonGroup size="small" fullWidth>
                          <Button
                            startIcon={<PlayArrow />}
                            onClick={() => handleOdometerAction(tracker.trackerId, 'start')}
                            disabled={loading === `${tracker.trackerId}-odometer-start`}
                          >
                            Start
                          </Button>
                          <Button
                            startIcon={<Pause />}
                            onClick={() => handleOdometerAction(tracker.trackerId, 'pause')}
                            disabled={loading === `${tracker.trackerId}-odometer-pause`}
                          >
                            Pause
                          </Button>
                          <Button
                            startIcon={<Stop />}
                            onClick={() => handleOdometerAction(tracker.trackerId, 'stop')}
                            disabled={loading === `${tracker.trackerId}-odometer-stop`}
                          >
                            Stop
                          </Button>
                        </ButtonGroup>
                      </AccordionDetails>
                    </Accordion>
                  )}


                  {/* Quick Commands */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 'auto' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Send fontSize="small" />
                        <Typography variant="body2">Quick Commands</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={1}>
                        {quickCommands.map((cmd) => (
                          <Button
                            key={cmd.command}
                            size="small"
                            variant="outlined"
                            startIcon={cmd.icon}
                            onClick={() => handleQuickCommand(tracker.trackerId, cmd.command)}
                            disabled={loading === `${tracker.trackerId}-quick-${cmd.command}`}
                            fullWidth
                          >
                            {cmd.label}
                          </Button>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        ))}

        {/* Global Controls */}
        <Box sx={{ mt: 2 }}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Global Controls
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<Send />}
                  onClick={() => setCommandDialog(true)}
                  disabled={!!loading}
                >
                  Send Custom Command
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => window.location.reload()}
                  disabled={!!loading}
                >
                  Refresh Dashboard
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Custom Command Dialog */}
      <Dialog open={commandDialog} onClose={() => setCommandDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Custom Command</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Tracker</InputLabel>
              <Select
                value={selectedTracker}
                onChange={(e) => setSelectedTracker(e.target.value)}
                label="Select Tracker"
              >
                {trackers.map((tracker) => (
                  <MenuItem key={tracker.trackerId} value={tracker.trackerId}>
                    {tracker.customTitle || tracker.currentRole} ({tracker.trackerId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="AT Command"
              value={customCommand}
              onChange={(e) => setCustomCommand(e.target.value)}
              placeholder="e.g., AT+GTRTL"
              fullWidth
              helperText="Enter a valid AT command (must start with AT+)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommandDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSendCommand}
            variant="contained"
            disabled={!selectedTracker || !customCommand.trim() || loading === 'custom-command'}
          >
            Send Command
          </Button>
        </DialogActions>
      </Dialog>

      {trackers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No trackers available for control
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ControlPanel;